/* Who the work belongs to. This phone holds the copy; the account holds the
 * name.
 *
 * **The first name syncs with the account, and it did not before V3.** It used
 * to be written on the phone it was typed on and never sent, and the cost of
 * that was paid on every second phone: the account knew there were two
 * children and could not say which was which, so a parent who had just signed
 * in was asked to name them again. The name now goes to ONE route,
 * `POST /v1/account/learners` and its `PATCH`, where the service stores it as
 * ciphertext and hands it back only to a verified session. It is in no other
 * request this app makes: not with a photograph, not with a typed question,
 * not in a URL, not in a log line. What the read still gets is the opaque id
 * and the school year.
 *
 * **This phone's copy is a cache and the account is the source.** Every screen
 * reads the local list, so a phone with no signal shows the right names and a
 * phone with no account shows them too. `restore` is what reconciles the two,
 * and it now prefers what the account sent.
 *
 * The id is minted by the service, never here. It used to be minted on the
 * device and the write side took whatever arrived, so the free allowance was
 * keyed on a string anyone could invent: a fresh id per request bought a fresh
 * allowance per request. This file no longer knows how to make one.
 *
 * The id is random rather than derived from the name, so two Sanas in one house
 * are two learners and one Sana renamed is still the same thread.
 *
 * The cost of keeping a copy here, stated because it is real: clearing site
 * data loses it. The pages survive, keyed on the image bytes and on the learner
 * id, and signing in brings both the ids and the names back.
 */
import { useSyncExternalStore } from "react";
import { attachLearner, eraseLearner, issueLearner } from "./api";
import type { AccountLearner, ExamBoard, ExamTier, Nation } from "./types";

const KEY = "mh.learners";
const CURRENT = "mh.current";
/** Changes made on this phone that the account has not been told about yet.
 *
 *  One queue for the year and the name rather than two keys. The year queue
 *  already had the three things a name queue needs: it survives the tab
 *  closing, it drains at launch, and it is emptied when this phone forgets a
 *  sign-in. That last one is why they share a key. An unsent name is the more
 *  sensitive of the two, and a second queue would be a second place to
 *  remember to empty. The old string-valued shape is still read, so a phone
 *  upgrading with a year owed does not lose it. */
const OWED = "mh.years_owed";

export interface Learner {
  /** Exactly the string the service issued. 64 hex characters, half of it a
   *  signature. Stored as given, sent as given, never parsed here. */
  child_id: string;
  /** The copy on this phone. The account holds the same string as ciphertext
   *  and is the source a second phone reads from. */
  name: string;
  /** School year as typed, for example "8". Free text: Year 8, S2 and P7 are
   *  all real answers and none of them is worth a dropdown on a phone. */
  year: string;
  /** Set only when a parent chose a nation other than England. */
  nation?: Nation;
  /** The exam board this child sits, set only when a parent chose one. It
   *  decides which specification's statements the result screen shows first
   *  and which it folds away; absent, every board is folded together. The
   *  account holds it too, under `exam_board`. */
  board?: ExamBoard;
  /** Foundation or Higher, set only when a parent chose one.
   *
   *  The account holds it too, under `tier`, and the account is the source: a
   *  parent who enters a child for Higher on one phone reads Higher on the
   *  other. This copy is what every screen reads, so it stands alone until the
   *  account answers and `adoptExams` writes the account's copy over it.
   *
   *  A learner with no tier is not a learner with a wrong one. The screen shows
   *  both tiers and labels each row rather than guessing one. */
  tier?: ExamTier;
  added_at: number;
  /** This learner arrived from an account rather than from a parent typing a
   *  name on this phone. It stays set after the naming: it is the one case
   *  where the name on the device was never watched being typed, so it is the
   *  one case where offering to change it is worth a control. */
  restored?: boolean;
  /** No account has ever been told about this learner.
   *
   *  **It is what stops one parent's children reaching another parent's
   *  account.** `session.forget` empties the owed queue for exactly that
   *  reason, found by the codex pass of 5 September 2026, and `oweUnattached`
   *  would put the same debt straight back: a phone that signed out and is
   *  then signed into by somebody else would send the first parent's children,
   *  their years and their first names up under the second parent's account.
   *
   *  Set when the learner is minted, cleared the moment an account takes it.
   *  Absent on a learner written before this field existed, and absence is
   *  read as "attachable": there is no installed base, and a device that
   *  predates the field and has never signed in is exactly the case
   *  `oweUnattached` exists for. An explicit false is never re-offered.
   *
   *  What it does not settle: a parent who signs out, adds a child, and hands
   *  the phone to somebody else who signs in. That child is owed to the second
   *  account, and there is nothing on the device that says whose it is. */
  unattached?: boolean;
}

/** The placeholder a learner wears until the parent names them.
 *
 *  A literal stored name rather than an empty string with a fallback in every
 *  screen that prints one. The alternative was a helper threaded through the
 *  home header, the learner list, the tab bar and the deletion sentence, which
 *  is ten call sites that each have to remember the blank case. */
export const UNNAMED = "Learner";

export interface Held {
  learners: Learner[];
  currentId: string | null;
}

const listeners = new Set<() => void>();

let snapshot: Held = read();

function read(): Held {
  let learners: Learner[] = [];
  let currentId: string | null = null;
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    if (Array.isArray(parsed)) {
      learners = parsed.filter((l): l is Learner =>
        !!l && typeof (l as Learner).child_id === "string");
    }
    currentId = localStorage.getItem(CURRENT);
  } catch {
    // Private browsing, a full quota, or a value somebody else wrote. An empty
    // list is the honest answer and the naming screen takes it from there.
  }
  return { learners, currentId: currentId || null };
}

function write(held: Held): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(held.learners));
    if (held.currentId) localStorage.setItem(CURRENT, held.currentId);
    else localStorage.removeItem(CURRENT);
  } catch {
    // Everything still works for this session; only the thread across visits
    // is lost.
  }
  snapshot = { learners: held.learners, currentId: held.currentId };
  for (const fn of listeners) fn();
}

export function all(): Learner[] {
  return snapshot.learners;
}

/** The learner whose work is being looked at. The first one where nothing has
 *  been chosen, because one child on a phone is the ordinary case and asking
 *  which of one is a screen for nothing. */
export function current(): Learner | null {
  const { learners, currentId } = snapshot;
  return learners.find((l) => l.child_id === currentId) ?? learners[0] ?? null;
}

export function choose(childId: string): Learner | null {
  const learner = snapshot.learners.find((l) => l.child_id === childId);
  if (!learner) return null;
  write({ ...snapshot, currentId: childId });
  return learner;
}

/** How long a name may be, matching the service's own 1 to 40. Held here so
 *  the field, the trim and the spec read one number. */
export const NAME_MAX = 40;

/** Name a child, against an id the service issues.
 *
 *  Asynchronous, and it can fail. That is the honest shape: without an id there
 *  is no meter to read against, so a service that cannot be reached cannot name
 *  a learner either. The caller shows the refusal the way it shows any other.
 *
 *  Nothing is sent to the ACCOUNT here. The caller attaches, because it is the
 *  caller that holds the session, and a failure to attach is a different
 *  sentence from a failure to mint an id. */
export async function add(name: string, year: string, nation?: Nation): Promise<Learner> {
  const issued = await issueLearner();
  const learner: Learner = {
    child_id: issued.child_id,
    name: name.trim().slice(0, NAME_MAX) || UNNAMED,
    year: year.trim().slice(0, 16),
    ...(nation && nation !== "england" ? { nation } : {}),
    added_at: Date.now(),
    unattached: true,
  };
  write({
    learners: [...snapshot.learners, learner],
    currentId: learner.child_id,
  });
  return learner;
}

/** Rewrite one stored learner in place, keeping its position in the list. */
function stamp(childId: string, patch: Partial<Learner>): Learner | null {
  const at = snapshot.learners.findIndex((l) => l.child_id === childId);
  const existing = snapshot.learners[at];
  if (at < 0 || !existing) return null;
  const updated: Learner = { ...existing, ...patch };
  const learners = [...snapshot.learners];
  learners[at] = updated;
  write({ learners, currentId: snapshot.currentId });
  return updated;
}

/** Set or clear the exam board this child sits.
 *
 *  Writes this phone's copy, the same shape as the year: the caller pushes
 *  `exam_board` to the account through `patchAccountLearner`, because it is the
 *  caller that holds the session. Passing null clears it, which is a parent
 *  saying they do not know rather than a parent saying nothing. */
export function setBoard(childId: string, board: ExamBoard | null): Learner | null {
  return stamp(childId, { board: board ?? undefined });
}

/** Set or clear the tier. The same shape as the board: this phone's copy here,
 *  the account's through `patchAccountLearner`. */
export function setTier(childId: string, tier: ExamTier | null): Learner | null {
  return stamp(childId, { tier: tier ?? undefined });
}

/** Change a name, or set the one a restored learner never had.
 *
 *  The local write stands on its own and the push is owed rather than awaited,
 *  the same shape the year takes and for the same reason: every screen on this
 *  phone reads the local copy, so a parent who renames a child on a train sees
 *  the new name at once, and what is waiting is the account's copy of it. A
 *  blank is a real answer and is stored as the placeholder; the account is
 *  told nothing in that case, because a placeholder is this phone's word for
 *  "not answered" and not a name to write over another phone's. */
export function rename(childId: string, name: string): Learner | null {
  const held = name.trim().slice(0, NAME_MAX);
  const updated = stamp(childId, { name: held || UNNAMED });
  if (updated && held) oweName(childId, held);
  return updated;
}

/** Set or change a child's school year after they were named.
 *
 *  The year is the one collected field that changes an outcome rather than a
 *  label: it narrows the lesson vocabulary the read picks a topic from, so a
 *  stale one is worse than a blank one.
 *
 *  **The account holds it too, and used to be told nothing.** The year travels
 *  with each upload, so the next page read on this phone uses the new one
 *  either way; what went stale was the account's copy, which is what a second
 *  phone restores from and what the walk found still reading 9 after a child
 *  was moved to 10. So the local write stands on its own and the push is owed
 *  rather than awaited: the sheet closes on the write, and `syncYears` sends
 *  what is owed the next time the account is reachable. */
export function setYear(childId: string, year: string): Learner | null {
  const held = year.trim().slice(0, 16);
  const updated = stamp(childId, { year: held });
  if (updated) oweYear(childId, held);
  return updated;
}

/* ------------------------------------------ what the account has not been told
 *
 * A queue on the device rather than a retry in memory. A parent who edits a
 * year on the train, closes the app and opens it at home is the ordinary case,
 * and an in-memory retry loses that edit at the moment the tab does. Storage
 * throws in private browsing, and the whole of the loss there is that the
 * account keeps a stale copy, so every access is wrapped and none of it is
 * worth a message on a screen.
 *
 * The value used to be the year as a string and now holds both fields. The
 * reader takes either shape, so a phone that had a year owed when it upgraded
 * still sends it.
 */

/** One child's unsent changes. Each field is absent where nothing about it is
 *  owed, which is what lets a name and a year be owed independently and sent
 *  together. */
export interface Owed {
  year?: string;
  name?: string;
}

function owed(): Record<string, Owed> {
  try {
    const raw = localStorage.getItem(OWED);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const held: Record<string, Owed> = {};
    for (const [childId, value] of Object.entries(parsed as Record<string, unknown>)) {
      /* The shape this key held before V3. A bare string was the year. */
      if (typeof value === "string") {
        held[childId] = { year: value };
        continue;
      }
      if (!value || typeof value !== "object" || Array.isArray(value)) continue;
      const row = value as Record<string, unknown>;
      const entry: Owed = {};
      if (typeof row.year === "string") entry.year = row.year;
      if (typeof row.name === "string") entry.name = row.name;
      if (entry.year !== undefined || entry.name !== undefined) held[childId] = entry;
    }
    return held;
  } catch {
    return {};
  }
}

function writeOwed(held: Record<string, Owed>): void {
  try {
    if (Object.keys(held).length === 0) localStorage.removeItem(OWED);
    else localStorage.setItem(OWED, JSON.stringify(held));
  } catch {
    /* the account keeps a stale copy, and nothing else is affected */
  }
}

function owe(childId: string, patch: Owed): void {
  const held = owed();
  writeOwed({ ...held, [childId]: { ...held[childId], ...patch } });
}

/** Record that the account has not been told this child's year. Exported for
 *  the screen that mints a learner and cannot attach it, which owes the same
 *  debt from the other direction. */
export function oweYear(childId: string, year: string): void {
  owe(childId, { year });
}

/** The same for the name. Exported for the same screen: a learner minted with
 *  a name on a phone that could not reach the account owes both. */
export function oweName(childId: string, name: string): void {
  owe(childId, { name });
}

/** Drop everything owed, unsent.
 *
 *  Called when this phone forgets a sign-in. The queue is a debt to ONE
 *  account and it is keyed on the child alone, so a phone that signs out and
 *  is then signed into by somebody else would have drained the first parent's
 *  children onto the second parent's account: their child ids, their school
 *  years and, since V3, their first names, under an account that has nothing
 *  to do with them. Found by the codex pass over the merged app diff on 5
 *  September 2026, and made reachable by the launch drain added the same day,
 *  which is what turned "nobody reopened Account" into "the app opened". The
 *  name landing in the queue makes it matter more, not less.
 *
 *  Nothing a parent can see is lost. The name and the year on this phone are
 *  the ones every screen and every read use and they stay in `mh.learners`;
 *  what goes is an unsent copy for an account this phone no longer holds. */
export function dropOwedYears(): void {
  writeOwed({});
}

/** What the account has not been told, per child. For a screen that wants to
 *  say so, and for the tests. */
export function yearsOwed(): Record<string, Owed> {
  return owed();
}

/** Owe the account every child on this phone it has never heard of.
 *
 *  **The ordinary first run leaves the account with nothing.** A parent
 *  photographs a page, consents, names a child and takes the picture, all
 *  before there is a session; the learner is minted with no account behind it.
 *  Signing in afterwards used to write the session and navigate, and nothing
 *  joined the two: no attach, so no sign-up grant, no name on the account and
 *  nothing for a second phone to restore. The audit of 5 September 2026 walked
 *  it and found the account screen reading "0 credits" under a landing
 *  offering ten free pages (FP1-01).
 *
 *  It writes the debt rather than sending it, and the caller drains. That is
 *  the shape every other unsent change in this file takes and it is the one
 *  that survives a bad connection: a drain that does not land leaves the debt,
 *  and `startYearSync` sends it at the next launch or the next time the phone
 *  comes back online.
 *
 *  **Only the children the account has not got.** Re-owing a child the account
 *  already holds would push this phone's copy of the name over whatever their
 *  other phone settled on, which is the exact loss `drainOwed` and `restore`
 *  are written to avoid. A placeholder is not a name and is not owed, for the
 *  reason `rename` gives.
 *
 *  Answers how many children are now owed, so the caller can skip the drain
 *  when there is nothing to send. */
export function oweUnattached(accountLearners: AccountLearner[]): number {
  const there = new Set(accountLearners.map((l) => l.child_id));
  let joining = 0;
  for (const held of snapshot.learners) {
    if (there.has(held.child_id)) continue;
    /* Never a learner some account already took. See `Learner.unattached`:
       without this, signing out and signing in as somebody else would send the
       first parent's children up under the second parent's account, which is
       the thing `session.forget` empties the queue to prevent. */
    if (held.unattached === false) continue;
    oweYear(held.child_id, held.year);
    if (held.name && held.name !== UNNAMED) oweName(held.child_id, held.name);
    joining += 1;
  }
  return joining;
}

/** Take the board and the tier the account holds and write them onto this
 *  phone's copies.
 *
 *  **The account is the source and this phone is the cache**, the same rule the
 *  name follows. A parent who enters a child for Higher on their phone and then
 *  opens the app on the other one should read Higher there, and before this the
 *  second phone kept whatever it had last been told locally, for ever.
 *
 *  The device value is the fallback and not the loser: it stands on its own
 *  until the account answers, which is what makes the edit sheet usable with no
 *  signal, and it is overwritten only where the account actually holds a value.
 *  An account row with a null board does NOT clear this phone's, because null
 *  is also what a service too old to store the field sends, and a client that
 *  read that as "the parent cleared it" would wipe a real answer on the first
 *  launch against an older service.
 *
 *  The cost of that choice, stated: clearing a board on one phone does not
 *  clear it on another until the parent clears it there too. Losing a set
 *  answer is the worse of the two mistakes, so this takes the other one.
 *
 *  Answers how many learners it changed, so a caller can skip a render. */
export function adoptExams(accountLearners: AccountLearner[]): number {
  let changed = 0;
  for (const sent of accountLearners) {
    const held = snapshot.learners.find((l) => l.child_id === sent.child_id);
    if (!held) continue;
    const patch: Partial<Learner> = {};
    const board = sent.exam_board as ExamBoard | null | undefined;
    if (board && board !== held.board) patch.board = board;
    if (sent.tier && sent.tier !== held.tier) patch.tier = sent.tier;
    if (Object.keys(patch).length === 0) continue;
    stamp(sent.child_id, patch);
    changed += 1;
  }
  return changed;
}

/** An account has this learner now. Called by whatever watched the attach
 *  succeed, so the learner is never offered to a second account. */
export function markAttached(childId: string): void {
  if (snapshot.learners.some(
    (l) => l.child_id === childId && l.unattached !== false)) {
    stamp(childId, { unattached: false });
  }
}

/** One drain at a time, across every caller.
 *
 *  There are three of them now: the Account screen on mount, the year sheet on
 *  close, and `startYearSync` at launch and on the online event. Two firing
 *  together both read the queue, both find the same debt and both send it, and
 *  the account then takes the same year twice. Harmless on the service, which
 *  treats an attach as a set, and still a request that should not exist.
 *
 *  A promise rather than a boolean, so the second caller waits on the first
 *  and gets its answer instead of being told nought was sent. */
let draining: Promise<number> | null = null;

/** Send everything the account has not been told about, and keep what does not
 *  land.
 *
 *  Answers how many children were settled. A failure is not reported anywhere:
 *  nothing a parent did has failed, the name and the year on this phone are the
 *  ones every screen and every read use, and the only thing waiting is a copy
 *  on the account. It is retried on the next visit to a screen that calls
 *  this. */
export function syncYears(token: string): Promise<number> {
  if (draining) return draining;
  draining = drainOwed(token).finally(() => { draining = null; });
  return draining;
}

async function drainOwed(token: string): Promise<number> {
  const held = owed();
  let sent = 0;
  for (const [childId, change] of Object.entries(held)) {
    try {
      /* Through the attach rather than the patch, deliberately. A learner
         minted while the phone had no signal was never attached at all, and a
         PATCH at that id would answer 404 and leave the debt for ever. The
         attach is a set and covers both cases.

         **Only the fields that are owed, and exactly the value that was
         queued.** The attach sets what it is given, so sending this phone's
         whole record would let a stale name overwrite a correction the parent
         made on their other phone. The queue says what changed here and it is
         rewritten on every edit, so two edits before one drain already queue
         the second: reading the list instead would add nothing and would make
         the account's copy depend on which of two stores was read. */
      await attachLearner(token, childId, change.year, change.name);
      markAttached(childId);
      sent += 1;
      const rest = owed();
      delete rest[childId];
      writeOwed(rest);
    } catch {
      /* Left owed. The next call sends it. */
    }
  }
  return sent;
}

/** Drain the queue at launch, and again whenever the phone comes back online.
 *
 *  Until this existed the only thing that sent an owed year was opening the
 *  Account screen or closing the sheet that edited one. So the ordinary case
 *  failed quietly: a parent corrects a year on the train, the push does not
 *  land, they never open Account again, and the second phone shows the old
 *  year for as long as that lasts. The year on THIS phone is always the one
 *  every read uses, so nothing a parent did has failed; what is stale is the
 *  copy the account holds, and the account is what a second phone restores
 *  from. C-B named it as the weakest part of its year work and left it for
 *  here because `lib` is nobody's screen.
 *
 *  Silent by construction. Nothing it does is worth a message: a failure
 *  leaves the year owed and the next launch sends it. It is not awaited, it
 *  swallows everything, and with no session there is no account to tell.
 *
 *  Answers its own teardown, so a caller that unmounts stops listening. Called
 *  once from `App`, which is the only thing in the app that runs at launch and
 *  outlives every route.
 *
 *  The token arrives as an accessor rather than a value, for two reasons. It
 *  has to be read at the moment of the drain and not at the moment of the
 *  subscription, because a phone that signs in an hour later is the ordinary
 *  case. And this file does not import `session`: the dependency runs the
 *  other way, so that signing out can clear the queue from `session.forget`
 *  without a cycle between the two. */
export function startYearSync(held: () => string | null): () => void {
  const drain = () => {
    const token = held();
    if (!token) return;
    if (Object.keys(owed()).length === 0) return;
    void syncYears(token).catch(() => {
      /* Left owed. The next launch, or the next time the phone comes back,
         sends it. */
    });
  };

  drain();
  if (typeof window === "undefined") return () => {};
  window.addEventListener("online", drain);
  return () => window.removeEventListener("online", drain);
}

/** Forget a learner on this device and nowhere else.
 *
 *  For the 401 recovery, where the service has stopped honouring an id and
 *  everything behind it was already unreachable. It is NOT the parent's delete
 *  control: that is `erase`, which asks the service first. Siblings are
 *  untouched, because two children on one phone is the ordinary case. */
export function remove(childId: string): void {
  const learners = snapshot.learners.filter((l) => l.child_id !== childId);
  const currentId = snapshot.currentId === childId
    ? learners[0]?.child_id ?? null
    : snapshot.currentId;
  write({ learners, currentId });
}

/** Remove everything for one learner, on the service and then here.
 *
 *  The order matters and it is this way round. Dropping the local record first
 *  would leave a parent who lost their connection mid-delete with pages on the
 *  service and no id left on the phone that can reach them, which is the one
 *  outcome a delete control must not produce. So the service goes first, and a
 *  refusal there leaves this device exactly as it was.
 *
 *  Answers false when the service says there is no such learner, which is also
 *  what a second tap on a control that already worked looks like. The local
 *  record goes in both cases. */
export async function erase(childId: string): Promise<boolean> {
  const gone = await eraseLearner(childId);
  remove(childId);
  const rest = owed();
  delete rest[childId];
  writeOwed(rest);
  return gone;
}

/* ----------------------------------------------------- restoring a new phone
 *
 * The account carries an id, a year, a page count, a purse and, since V3, the
 * first name. So a restored phone usually knows exactly who is who and has
 * nothing to ask. What it still has to handle is the family that predates the
 * sync, or a child added from a phone that never asked for a name: those rows
 * arrive without one, and they are the only rows the screen asks about.
 */

/** One row of the "Who is who?" screen: what the account knows, the name to
 *  put against it, and where that name came from. */
export interface Restorable {
  account: AccountLearner;
  /** The name to put against this row, or null when nobody holds one. Null is
   *  the row that needs an answer. */
  name: string | null;
  /** True when the name came back with the account rather than off this phone.
   *  The screen shows those as facts rather than as questions, and it is the
   *  one thing a parent needs to know about a name they did not just type. */
  synced: boolean;
}

/** Take the account's rows and say which of them need naming.
 *
 *  Nothing is written. This is the question the screen asks, and `adopt` is the
 *  answer. Every row is returned, named or not, so the screen can show the
 *  whole family rather than only the gaps.
 *
 *  **The account wins where it sent a name.** It is the source and this phone
 *  holds a cache, so a parent who corrected a name on their other phone reads
 *  the correction here rather than the stale copy. Where the account sent
 *  nothing, this phone's own name stands: that is the family named before the
 *  sync existed, whose names are on the phone that typed them, and the first
 *  drain sends them up.
 */
export function restore(accountLearners: AccountLearner[]): Restorable[] {
  return accountLearners.map((account) => {
    const sent = account.name?.trim();
    if (sent) return { account, name: sent, synced: true };
    const known = snapshot.learners.find(
      (l) => l.child_id === account.child_id);
    return {
      account,
      name: known && known.name !== UNNAMED ? known.name : null,
      synced: false,
    };
  });
}

/** Write a learner the account knows about onto this device.
 *
 *  Deliberately no `POST /v1/learners`: the service issued this id already,
 *  on another phone, and asking for a second one would strand the pages this
 *  whole account exists to find.
 *
 *  A blank name is a real answer and is stored as `UNNAMED`. Five learners and
 *  five required fields between a parent and the pages they just signed in to
 *  reach would be a wall, and there are no walls on this path.
 *
 *  **Nothing is owed to the account here, and that is deliberate.** The screen
 *  that calls this decides what to send: a name the account itself just sent
 *  back must not be queued straight back to the account, and a name this
 *  parent has just typed must be. Owing every row would send the whole family
 *  up again on every sign-in.
 */
export function adopt(childId: string, name: string, year: string): Learner {
  const existing = snapshot.learners.find((l) => l.child_id === childId);
  const learner: Learner = {
    ...(existing ?? {}),
    child_id: childId,
    name: name.trim().slice(0, NAME_MAX) || existing?.name || UNNAMED,
    year: year.trim().slice(0, 16),
    added_at: existing?.added_at ?? Date.now(),
    restored: true,
    /* It came off an account, so an account holds it. */
    unattached: false,
  };
  const learners = existing
    ? snapshot.learners.map((l) => (l.child_id === childId ? learner : l))
    : [...snapshot.learners, learner];
  write({ learners, currentId: snapshot.currentId ?? learner.child_id });
  return learner;
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function get(): Held {
  return snapshot;
}

export interface Learners {
  learners: Learner[];
  current: Learner | null;
}

export function useLearners(): Learners {
  const held = useSyncExternalStore(subscribe, get, get);
  return {
    learners: held.learners,
    current: held.learners.find((l) => l.child_id === held.currentId)
      ?? held.learners[0] ?? null,
  };
}
